<template>
  <div
    class="booking-handler"
    v-if="
      (selectedTravel && (!returnTravels.length || hasValidReturnTravel)) ||
      (selectedTravel && returnTravels.length && !hasValidReturnTravel)
    "
  >
    <Button
      v-if="selectedTravel && (!returnTravels.length || hasValidReturnTravel)"
      @click="handleBooking"
      theme="primary"
      class="booking-handler__button"
      :disabled="!selectedTravel"
    >
      {{ $t('common.actions.confirmContinue') }}
    </Button>
    <Button
      v-if="selectedTravel && returnTravels.length && !hasValidReturnTravel"
      @click="showInboundTravels"
      theme="primary"
      class="booking-handler__button"
    >
      {{ $t('booking.selectReturn') }}
    </Button>
  </div>
</template>

<script lang="ts">
  import { defineComponent, ref, PropType, watch, computed } from 'vue'
  import { useRouter } from 'vue-router'
  import { useI18n } from 'vue-i18n'
  import { bookJourney } from '@/services/journeyService'
  import { useJourneyStore } from '@/stores/journeyStore'
  import { useBookingStore } from '@/stores/bookingStore'
  import { useErrorStore } from '@/stores/errorStore'
  import { Booking } from '@/types/bookingTypes'
  import { Travel } from '@/types/travelTypes'
  import { Passenger } from '@/types/passengerTypes'
  import { Bundle } from '@/types/journeyTypes'
  import Button from '@/components/BaseComponents/Button.vue'

  export default defineComponent({
    name: 'BookingHandler',
    components: {
      Button,
    },
    props: {
      selectedTravel: {
        type: Object as PropType<Travel | null>,
        required: false,
        default: null,
      },
      selectedReturnTravel: {
        type: Object as PropType<Travel | null>,
        required: false,
        default: null,
      },
      returnTravels: {
        type: Array as PropType<Travel[]>,
        required: false,
        default: () => [],
      },
      showInboundTravels: {
        type: Function as PropType<() => void>,
        required: false,
        default: () => {},
      },
      selectedBundle: {
        type: Object as PropType<Bundle | null>,
        required: false,
        default: null,
      },
      selectedRouteIndex: {
        type: Number,
        required: false,
        default: null,
      },
      selectedBundleIndex: {
        type: Number,
        required: false,
        default: null,
      },
    },
    setup(props) {
      const { t } = useI18n()
      const journeyStore = useJourneyStore()
      const bookingStore = useBookingStore()
      const errorStore = useErrorStore()
      const router = useRouter()
      const bookingStarted = ref(false)

      watch(() => props.selectedReturnTravel, (newValue) => {
        if (newValue && props.selectedRouteIndex !== null && props.selectedBundleIndex !== null) {
          const returnTravel = props.returnTravels.find(
            (travel) => travel?.direction === 'inbound'
          )
          if (returnTravel && 
              returnTravel.routes && 
              returnTravel.routes[props.selectedRouteIndex] &&
              returnTravel.routes[props.selectedRouteIndex]?.bundles?.[props.selectedBundleIndex]) {
            bookingStore.setInboundTravel(
              returnTravel,
              props.selectedRouteIndex,
              props.selectedBundleIndex
            )
          }
        }
      })

      const hasValidReturnTravel = computed(() => {
        if (!props.returnTravels.length) {
          return true
        }
        return (props.selectedReturnTravel && bookingStore.inboundTravel) || (bookingStore.inboundTravel && bookingStore.segments.some(s => s.direction === 'inbound'))
      })

      const handleBooking = async () => {
        if (!props.selectedTravel) {
          errorStore.setError(t('travelPlanner.booking.errors.noTravel'))
          return
        }
        bookingStarted.value = true
        errorStore.clearError()
        
        const passengers =
          journeyStore.passengers.map((passenger: Passenger) => ({
            id: passenger.id,
            type: passenger.type,
            discount_cards: passenger.discount_cards,
          })) || []

        bookingStore.setPassengers(passengers)

        try {
          const response = await bookJourney()
          journeyStore.setBookingData(response.data.booking)
          bookingStore.setBookingNumber(response.data.booking.booking_number)
          bookingStore.setBookingData(response.data.booking)
          bookingStore.setBooking(response.data)

          const hasSeats = response.data.booking.outbound_booking_tariff_segments.some((segment: { required_products: Array<{ seat: boolean }> }) => 
            segment.required_products.some((product: { seat: boolean }) => product.seat)
          ) || response.data.booking.inbound_booking_tariff_segments?.some((segment: { required_products: Array<{ seat: boolean }> }) => 
            segment.required_products.some((product: { seat: boolean }) => product.seat)
          )

          if (hasSeats) {
            await router.push({ name: 'SeatReservation' })
          } else {
            await router.push({ name: 'EquipmentPage' })
          }
        } catch (error: any) {
          console.error('Booking error:', error)
          if (error?.name === 'NavigationFailure') {
            return
          }
          
          if (error?.response?.data?.error) {
            errorStore.setError(error.response.data.error.message)
          } else {
            errorStore.setError(t('travelPlanner.booking.errors.unexpected'))
          }
        }
      }

      return {
        handleBooking,
        bookingStarted,
        hasValidReturnTravel,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .booking-handler {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    &__button {
      align-self: flex-start;
    }

    @media (max-width: 440px) {
      &__button {
        align-self: stretch;
      }
    }
  }
</style>
